import moment from "moment";
import {computeReadinessIndex} from "@/utils/playerIndices";

export const numeric = (value, min, max) => {
	if (value == null || value === "") return null;
	const parsed = parseFloat(String(value).replace(",", "."));
	return Number.isFinite(parsed) && parsed >= min && parsed <= max
		? parsed
		: null;
};
const average = (values) =>
	values.length
		? values.reduce((sum, value) => sum + value, 0) / values.length
		: null;
export const matchSide = (match, rosterId) =>
	match.homeRosterId === rosterId
		? "home"
		: match.awayRosterId === rosterId
			? "away"
			: null;
export const eventFinished = (event, now) => {
	if (!event.startDate || !moment(event.startDate).isValid()) return false;
	if (
		event.startTimeToBeConfirmed &&
		moment(event.startDate).isSame(now, "day")
	)
		return false;
	const end =
		event.endDate ||
		moment(event.startDate).add(event.sportingMinutes || 90, "minutes");
	return moment(end).isSameOrBefore(now);
};
export function rosterMatches(matches, rosterId, now) {
	const relevant = matches.filter((match) => matchSide(match, rosterId));
	const past = relevant
		.filter((match) => eventFinished(match, now))
		.sort((a, b) => moment(b.startDate) - moment(a.startDate))
		.slice(0, 5)
		.map((match) => {
			const side = matchSide(match, rosterId);
			const home = match.result ? match.result.home : match.homeClubGoals;
			const away = match.result ? match.result.away : match.awayClubGoals;
			const conflict = !!match.result?.conflict;
			const scored = !conflict && home != null && away != null;
			const ours = side === "home" ? home : away;
			const theirs = side === "home" ? away : home;
			return {
				...match,
				side,
				opponent:
					side === "home"
						? match.awayTeamName || match.awayClub
						: match.homeTeamName || match.homeClub,
				score: scored ? `${home}–${away}` : null,
				conflict,
				outcome: scored
					? ours > theirs
						? "V"
						: ours < theirs
							? "S"
							: "N"
					: null,
			};
		});
	const next =
		relevant
			.filter(
				(match) =>
					moment(match.startDate).isAfter(now) ||
					(match.startTimeToBeConfirmed &&
						moment(match.startDate).isSame(now, "day")),
			)
			.sort((a, b) => moment(a.startDate) - moment(b.startDate))[0] || null;
	return {past, next};
}
export function rosterTrainings(trainings, rosterId, now) {
	return trainings.filter(
		(training) =>
			training.rosterId === rosterId && eventFinished(training, now),
	);
}
export function readinessForPlayers(
	players,
	medical,
	tqr,
	rpe,
	now,
	medicalAvailable = true,
) {
	const playerIds = new Set(players.map((player) => player.id));
	const injured = new Set();
	medical.forEach((record) => {
		const playerId = record.playerId || record.playerData?.id;
		if (!playerIds.has(playerId)) return;
		if (
			(record.injuriesIllness || []).some(
				(injury) =>
					!injury.recordType &&
					(!injury.startDate || moment(injury.startDate).isSameOrBefore(now)) &&
					(!injury.sportsDischargeDate ||
						moment(injury.sportsDischargeDate).isAfter(now)),
			)
		)
			injured.add(playerId);
	});
	const since = moment(now).subtract(6, "days").startOf("day");
	const recent = (date, dateOnly = false) =>
		date &&
		moment(date).isValid() &&
		moment(date).isSameOrAfter(since) &&
		(dateOnly
			? moment(date).isSameOrBefore(now, "day")
			: moment(date).isSameOrBefore(now));
	const readiness = {};
	players.forEach((player) => {
		const tqrRows = tqr.filter(
			(row) =>
				row.playerId === player.id &&
				recent(row.tqrDate, true) &&
				numeric(row.tqrValue, 6, 20) != null,
		);
		const rpeRows = rpe.filter(
			(row) =>
				row.playerId === player.id &&
				recent(row.rpeDate) &&
				numeric(row.rpe, 1, 10) != null,
		);
		const avgTQR = average(tqrRows.map((row) => numeric(row.tqrValue, 6, 20)));
		const avgRPE = average(rpeRows.map((row) => numeric(row.rpe, 1, 10)));
		const last = (rows, key) =>
			rows.map((row) => row[key]).sort((a, b) => moment(b) - moment(a))[0] ||
			null;
		readiness[player.id] = {
			...(medicalAvailable
				? computeReadinessIndex({
						avgTQR,
						avgRPE,
						isInjured: injured.has(player.id),
					})
				: {value: null, color: "gray", label: "Dati medici non disponibili"}),
			avgTQR,
			avgRPE,
			tqrCount: tqrRows.length,
			rpeCount: rpeRows.length,
			lastTqr: last(tqrRows, "tqrDate"),
			lastRpe: last(rpeRows, "rpeDate"),
			injured: injured.has(player.id),
		};
	});
	return {readiness, injured};
}
export function trainingSummary(trainings, players, now) {
	const weekStart = moment(now).subtract(6, "days").startOf("day");
	const weekly = Array.from({length: 7}, (_, index) => ({
		date: moment(weekStart).add(index, "days").format("YYYY-MM-DD"),
		samples: [],
		sessions: 0,
		present: 0,
	}));
	const attendance = players.map((player) => {
		let present = 0,
			known = 0;
		trainings.forEach((training) => {
			const status = (training.trainingConvocations || []).find(
				(row) => row.playerId === player.id,
			)?.status;
			if (["Presente", "Assente", "Infortunato"].includes(status)) known++;
			if (status === "Presente") present++;
		});
		return {
			player,
			present,
			known,
			total: trainings.length,
			unknown: trainings.length - known,
		};
	});
	const playerIds = new Set(players.map((p) => p.id));
	trainings.forEach((training) => {
		const day = weekly.find(
			(row) => row.date === moment(training.startDate).format("YYYY-MM-DD"),
		);
		if (!day) return;
		day.sessions++;
		const presentIds = new Set(
			(training.trainingConvocations || [])
				.filter(
					(row) => row.status === "Presente" && playerIds.has(row.playerId),
				)
				.map((row) => row.playerId),
		);
		day.present += presentIds.size;
		(training.trainingRPE || []).forEach((row) => {
			const value = numeric(row.rpe, 1, 10);
			if (presentIds.has(row.playerId) && value != null)
				day.samples.push(value);
		});
	});
	return {
		attendance,
		weekly: weekly.map(({samples, ...day}) => ({
			...day,
			label: moment(day.date).format("DD/MM"),
			avgRPE: average(samples),
			measured: samples.length,
		})),
	};
}
export function minutesSummary(players, matches, statistics) {
	return players.map((player) => {
		let minutes = 0,
			known = 0,
			conflicts = 0;
		matches.forEach((match) => {
			const row = (statistics[match.id] || []).find(
				(entry) =>
					entry.playerId === player.id &&
					(!entry.matchSide || entry.matchSide === match.side),
			);
			const metric = row?.metrics?.minutes;
			if (metric?.conflict) conflicts++;
			else if (metric?.value != null) {
				minutes += metric.value;
				known++;
			}
		});
		return {
			player,
			minutes: known ? minutes : null,
			known,
			total: matches.length,
			conflicts,
		};
	});
}
